/**
 * Pure row filter/sort predicates for the redesigned client-offer-review
 * unit list — extracted to keep `use-client-offer-review-view-state.ts`'s
 * `rows` useMemo callback's complexity low. Mirrors the mockup's
 * `use-offer-review-state-helpers.ts` (`matchesRowFilter`), adapted for
 * real `ClientOfferReviewUnit` data where `supplierNumber` is `null` on
 * both client-facing routes (OQ-5/Decision 6).
 */
import {
  matchesClientOfferReviewPanelFilters,
  type ClientOfferReviewDecision,
  type ClientOfferReviewFilterKey,
  type ClientOfferReviewSortKey,
  type ClientOfferReviewUnit,
} from '../../client-offer-review';

/** Whether a unit's effective decision (sold takes precedence) matches the active filter tab. */
function matchesFilterTab(
  effectiveDecision: 'sold' | ClientOfferReviewDecision,
  filter: ClientOfferReviewFilterKey,
): boolean {
  if (filter === 'ACCEPTED') return effectiveDecision === 'accepted';
  if (filter === 'REJECTED') return effectiveDecision === 'rejected';
  if (filter === 'PENDING') return effectiveDecision === null;
  return true;
}

/**
 * Whether a unit's searchable fields contain the (already-lowercased) query.
 * `supplierNumber` is `null` on both client-facing routes — `?? ''` avoids
 * matching every row against the literal string "null".
 */
function matchesSearchQuery(unit: ClientOfferReviewUnit, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  return `${unit.address} ${unit.city} ${unit.grw} ${unit.supplierNumber ?? ''}`
    .toLowerCase()
    .includes(normalizedQuery);
}

/** Composes (AND) the status tab, free-text search, and active FilterPanel selections. */
export function matchesReviewRowFilter(
  unit: ClientOfferReviewUnit,
  decisions: Record<string, ClientOfferReviewDecision>,
  filter: ClientOfferReviewFilterKey,
  normalizedQuery: string,
  panelFilters: Record<string, string[]>,
): boolean {
  const effectiveDecision = unit.sold ? 'sold' : (decisions[unit.id] ?? null);
  return (
    matchesFilterTab(effectiveDecision, filter) &&
    matchesSearchQuery(unit, normalizedQuery) &&
    matchesClientOfferReviewPanelFilters(unit, panelFilters)
  );
}

/** Sold last, accepted first, then rejected, then pending — used by the `'decision'` sort key. */
function decisionRank(unit: ClientOfferReviewUnit, decision: ClientOfferReviewDecision): number {
  if (unit.sold) return 3;
  if (decision === 'accepted') return 0;
  if (decision === 'rejected') return 2;
  return 1;
}

/** Comparator for the active `sortKey` — city (default), price, or decision rank. */
export function compareReviewRows(
  a: ClientOfferReviewUnit,
  b: ClientOfferReviewUnit,
  sortKey: ClientOfferReviewSortKey,
  decisions: Record<string, ClientOfferReviewDecision>,
): number {
  if (sortKey === 'price') return (a.price ?? 0) - (b.price ?? 0);
  if (sortKey === 'decision') {
    const rankDiff =
      decisionRank(a, decisions[a.id] ?? null) - decisionRank(b, decisions[b.id] ?? null);
    return rankDiff || a.city.localeCompare(b.city, 'pl');
  }
  return a.city.localeCompare(b.city, 'pl') || a.address.localeCompare(b.address, 'pl');
}
